import React, { useState, useRef } from 'react';
import { TextField, Button, Container, Box, Card, CardContent, CircularProgress, Alert, Typography, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import Sticker from '../common/Sticker';
import { API_URL } from '../../config';

const StickerViewV2 = ({ onBackClick }) => {
  const [container, setContainer]   = useState('');
  const [stickers, setStickers]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [isLandscape, setIsLandscape] = useState(true);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: ' ',
    pageStyle: `
      @page { size: ${isLandscape ? 'A4 landscape' : 'A4 portrait'}; margin: ${isLandscape ? '3mm' : '10mm'}; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .MuiContainer-root, .MuiGrid-root { padding: 0 !important; margin: 0 !important; }
        .sticker-print-page { page-break-after: always !important; break-after: page !important; page-break-inside: avoid !important; break-inside: avoid !important; }
        .sticker-print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `
  });

  const FSC_ALLOWED_FORESTS = ['ROUGIER', 'ROUGIER-GSEZ', 'ROUGIER-W', 'PW-CEB'];

  const handleExcelDownload = () => {
    if (!stickers.length) return;
    // Exclude blocked stickers from export
    const exportable = stickers.filter(s => !s.blocked);
    if (!exportable.length) return;
    const excelData = exportable.map((s, i) => ({
      'Sticker No.': i + 1, 'Container': container,
      'Pallet No.': s.palletNo, 'Quantity (PCS)': s.pcs, 'CBM': s.cbm,
      'Length (mm)': s.length, 'Width (mm)': s.width, 'Thickness (mm)': s.thickness,
      'Quality': s.quality, 'Species': s.species, 'Glue': s.glue, 'Class': s.productClass,
      'Source': s.sourceText || ''
    }));
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stickers');
    XLSX.writeFile(wb, `Stickers_V2_${container}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleSearch = async () => {
    setLoading(true); setError(''); setStickers([]);
    try {
      const res = await fetch(`${API_URL}/container/viewstickerv2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ container })
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();

      // V2 recordset indices (shifted by proc_pallettracingdataNewcont2 + lotCursor output):
      // [0] pallet data (proc_pallettracingdataNewcont2)
      // [1] pallet log connection (proc_pallettracingdataNewcont2)
      // [2] company info
      // [3] work order info
      // [4] main sticker rows  — each row includes PALLET_NO directly
      // [5] orderno/destination/consignee
      // [6] certificates
      // [7] summary (CBM, PCS, TOTAL_PALLETS)
      // [8] FOREST/AAC rows
      const companyInfo           = apiData.data?.[2]?.[0] || {};
      const mainHeader            = apiData.data?.[3]?.[0] || {};
      const mainStickerRows       = apiData.data?.[4] || [];
      const orderDestinationInfo  = apiData.data?.[5]?.[0] || {};
      const certificatesRow       = apiData.data?.[6]?.[0] || {};
      const forestAACRows         = apiData.data?.[8] || [];

      const destination = orderDestinationInfo.DESTINATION || '';
      const consignee   = orderDestinationInfo.CONSIGNEE   || '';

      const certificates = [
        certificatesRow.CERTIFICATE1, certificatesRow.CERTIFICATE2,
        certificatesRow.CERTIFICATE3, certificatesRow.CERTIFICATE4,
        certificatesRow.CERTIFICATE5
      ].filter(Boolean);

      // Build per-pallet FOREST/AAC map: { PALLET_NO -> ["FOREST AAC", ...] }
      const palletAACMap = {};
      forestAACRows.forEach(r => {
        if (!r.PALLET_NO || !r.AAC) return;
        const key = String(r.PALLET_NO);
        if (!palletAACMap[key]) palletAACMap[key] = [];
        const entry = `${r.FOREST} ${r.AAC}`;
        if (!palletAACMap[key].includes(entry)) palletAACMap[key].push(entry);
      });

      const stickerList = mainStickerRows.map((row) => {
        // Per-pallet FSC check:
        // Work order CERTIFICATION_LEGAL drives the blocking decision:
        //   'FSC 100%'       → validate forest per pallet, block if wrong forest
        //   'FSC & Non-FSC'  → never block (mixed container, client accepts both)
        //   'Non-FSC'        → never block
        // Per-pallet CERTIFICATES drives whether FSC cert line shows on the sticker.

        const workOrderCert = (mainHeader.CERTIFICATION_LEGAL || '').trim().toUpperCase();
        const isMixedOrNonFSC = workOrderCert !== 'FSC 100%'; // 'FSC & Non-FSC' or 'Non-FSC' → no blocking

        const palletCert = (row.CERTIFICATES || '').trim().toUpperCase();
        const isFSC = palletCert === 'FSC 100%'; // controls FSC cert line on sticker

        const allCertificates = [...certificates];
        if (isFSC) allCertificates.push('LEVEL CERTIFICATION : FSC 100%');

        // PALLET_NO now comes directly from the procedure result row
        const palletNo = row.PALLET_NO ? String(row.PALLET_NO) : '';

        // Get per-pallet FOREST AAC list
        const palletSourceText = palletAACMap[palletNo]
          ? palletAACMap[palletNo].join(', ')
          : '';

        // FSC 100% forest validation — only when work order is purely FSC 100%
        let blocked = false;
        let blockReason = '';
        if (isFSC && !isMixedOrNonFSC) {
          const forestRows = forestAACRows.filter(r => String(r.PALLET_NO) === palletNo);
          if (forestRows.length === 0) {
            blocked = true;
            blockReason = `Pallet ${palletNo}: No forest source data found. FSC 100% certificate requires forest to be ROUGIER, ROUGIER-GSEZ, ROUGIER-W or PW-CEB. Please correct the data before generating this sticker.`;
          } else {
            const invalidForests = [...new Set(
              forestRows
                .map(r => (r.FOREST || '').toUpperCase().trim())
                .filter(f => !FSC_ALLOWED_FORESTS.includes(f))
            )];
            if (invalidForests.length > 0) {
              blocked = true;
              blockReason = `Pallet ${palletNo}: FSC 100% certificate requires forest source to be ROUGIER, ROUGIER-GSEZ, ROUGIER-W or PW-CEB. Found: ${invalidForests.join(', ')}. Please correct the data before generating this sticker.`;
            }
          }
        }
        // Non-FSC / FSC & Non-FSC pallets: never blocked

        return {
          company: companyInfo.Company_Name || '',
          consignee, destination,
          length: row.LENGTH, width: row.WIDTH, thickness: row.THICKNESS,
          quality: row.QUALITY, pcs: row.PCS,
          cbm: row.CBM ? parseFloat(row.CBM).toFixed(3) : '0.000',
          species: row.SPECIES, glue: row.GLUE, productClass: row.CLASS,
          lotNumber: row.LOTNUMBER, totalPallets: row.TOTAL_PALLETS,
          orderNo: mainHeader.Work_OrderNo || '',
          certificates: allCertificates,
          sourceText: palletSourceText,
          palletNo,
          blocked,
          blockReason
        };
      });

      setStickers(stickerList);
      if (stickerList.length === 0) setError('No records found for this container.');
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Search Card */}
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', background: 'white', border: '1px solid #e0e0e0' }} className="print-hide">
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: '#1b4332', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <SearchIcon sx={{ fontSize: 28, color: '#1b4332', mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1b4332', letterSpacing: '-0.5px' }}>
              Sticker V2 — Forest/AAC Tracing
            </Typography>
          </Box>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="stretch">
            <TextField label="Enter Container Number" variant="outlined" value={container}
              onChange={e => setContainer(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !loading && container && handleSearch()}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, backgroundColor: '#fafafa', '& fieldset': { borderColor: '#e0e0e0' }, '&:hover fieldset': { borderColor: '#1b4332' }, '&.Mui-focused fieldset': { borderColor: '#1b4332', borderWidth: '1px' } }, '& .MuiInputLabel-root': { color: '#666' } }}
              InputProps={{ style: { fontSize: '1rem', padding: '4px' } }}
            />
            <Button variant="contained" size="large" startIcon={<SearchIcon />}
              disabled={loading || !container} onClick={handleSearch}
              sx={{ minWidth: 160, borderRadius: 1, fontSize: '0.95rem', fontWeight: 500, background: '#1b4332', color: 'white', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#0f2419', boxShadow: 'none' } }}>
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results Header */}
      {stickers.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }} className="print-hide">
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <InventoryIcon sx={{ fontSize: 32, color: '#1b4332' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1b4332' }}>
                    {stickers.filter(s => !s.blocked).length} Sticker{stickers.filter(s => !s.blocked).length !== 1 ? 's' : ''} Ready
                    {stickers.some(s => s.blocked) && (
                      <Box component="span" sx={{ ml: 1.5, color: '#c62828', fontSize: '0.95rem', fontWeight: 600 }}>
                        · {stickers.filter(s => s.blocked).length} Blocked (FSC)
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>Container: {container}</Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <FormControlLabel
                  control={<Checkbox checked={isLandscape} onChange={e => setIsLandscape(e.target.checked)} sx={{ color: '#1b4332', '&.Mui-checked': { color: '#1b4332' } }} />}
                  label="Landscape Printing"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.95rem', fontWeight: 500, color: '#1b4332' } }}
                />
                <Button variant="contained" size="large" startIcon={<DownloadIcon />} onClick={handleExcelDownload}
                  disabled={stickers.filter(s => !s.blocked).length === 0}
                  sx={{ minWidth: 160, borderRadius: 1, fontSize: '0.95rem', fontWeight: 500, background: '#2e7d32', color: 'white', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#1b5e20', boxShadow: 'none' } }}>
                  Excel
                </Button>
                <Button variant="contained" size="large" startIcon={<PrintIcon />} onClick={handlePrint}
                  disabled={stickers.filter(s => !s.blocked).length === 0}
                  sx={{ minWidth: 160, borderRadius: 1, fontSize: '0.95rem', fontWeight: 500, background: '#1b4332', color: 'white', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#0f2419', boxShadow: 'none' } }}>
                  Print All
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {loading && <Box textAlign="center" py={8} className="print-hide"><CircularProgress size={50} sx={{ color: '#1b4332' }} /><Typography variant="body1" sx={{ mt: 2, color: '#666', fontWeight: 500 }}>Searching container...</Typography></Box>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }} className="print-hide">{error}</Alert>}

      {/* Sticker Preview Grid */}
      {stickers.length > 0 && (
        <Box className="print-hide">
          <Typography variant="body2" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
            Preview of {stickers.length} sticker{stickers.length !== 1 ? 's' : ''}
            {stickers.some(s => s.blocked) && ' — blocked stickers cannot be printed or exported'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {stickers.map((sticker, idx) => (
              <Box key={idx} sx={{ width: 'calc(50% - 4px)', flexShrink: 0 }}>
                {sticker.blocked ? (
                  <Card sx={{ borderRadius: 1, border: '2px solid #c62828', background: '#fff8f8', p: 2, minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#c62828', fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
                      ⛔ STICKER BLOCKED — FSC 100% Validation Failed
                    </Typography>
                    <Typography sx={{ color: '#b71c1c', fontSize: '0.8rem', lineHeight: 1.5 }}>
                      {sticker.blockReason}
                    </Typography>
                  </Card>
                ) : (
                  <Card sx={{ borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', p: 0, m: 0, width: '100%', border: '1px solid #e0e0e0' }}>
                    <Box sx={{ transform: 'scale(0.48)', transformOrigin: 'top left', width: '208.33%', marginBottom: '-52%' }}>
                      <Sticker {...sticker} isLandscape={isLandscape} />
                    </Box>
                  </Card>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {stickers.length === 0 && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 2, background: '#fafafa', boxShadow: 'none', border: '1px dashed #e0e0e0' }} className="print-hide">
          <InventoryIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>No stickers to display</Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>Enter a container number above to get started</Typography>
        </Card>
      )}

      <div ref={printRef} className="print-only">
        {stickers.filter(s => !s.blocked).map((sticker, idx) => (
          <div key={idx} className={`sticker-print-page ${isLandscape ? 'sticker-landscape' : ''}`}>
            <Sticker {...sticker} isLandscape={isLandscape} />
          </div>
        ))}
      </div>
    </Container>
  );
};

export default StickerViewV2;


